import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import {
  allCapabilitiesEnabled, allFeaturesEnabled,
  type UiCapability, type UiCapabilityFlags, type UiFeature, type UiFeatureFlags,
} from '@repo/shared-types';
import { endAuth0Session, markAuth0Session } from './auth0';

export interface AuthUser {
  id: string;
  email: string;
  name: string | null;
  /**
   * Which UI sections this user may see. Arrives with the session rather than on its own
   * request, so the first paint is already correct instead of rendering every section and
   * then dropping the disabled ones.
   */
  features: UiFeatureFlags;
  /**
   * The switches *inside* those sections — a control or a column rather than a page.
   *
   * Already resolved against `features` by the server, so a capability of a hidden section is
   * already false here and callers never have to check both.
   */
  capabilities: UiCapabilityFlags;
  /** Whether the /admin console is available to this user. */
  isSystemAdmin: boolean;
}

interface AuthState {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  /** Resolved flags for the signed-in user; all-enabled while unauthenticated. */
  features: UiFeatureFlags;
  hasFeature: (feature: UiFeature) => boolean;
  capabilities: UiCapabilityFlags;
  /**
   * Whether one in-page capability is available. Presentation only — the API still returns the
   * underlying data, so this hides a control, it does not withhold a secret.
   */
  hasCapability: (capability: UiCapability) => boolean;
  isSystemAdmin: boolean;
  /** Exchanges an Auth0 access token for the console session — see `lib/auth0.ts`. */
  loginWithAuth0: (accessToken: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

async function authFetch(path: string, init?: RequestInit) {
  return fetch(`/api${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...init?.headers },
    ...init,
  });
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Restore session on mount via the HTTP-only cookie
  useEffect(() => {
    authFetch('/auth/me')
      .then((res) => (res.ok ? res.json() as Promise<AuthUser> : null))
      .then((u) => setUser(u))
      .catch(() => setUser(null))
      .finally(() => setIsLoading(false));
  }, []);

  const loginWithAuth0 = useCallback(async (accessToken: string) => {
    const res = await authFetch('/auth/auth0', {
      method: 'POST',
      body: JSON.stringify({ accessToken }),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => null) as { message?: string } | null;
      throw new Error(data?.message ?? 'Auth0 sign-in failed');
    }
    const data = await res.json() as { user: AuthUser };
    markAuth0Session();
    setUser(data.user);
  }, []);

  const logout = useCallback(async () => {
    await authFetch('/auth/logout', { method: 'POST' }).catch(() => null);
    setUser(null);
    // Last, because it navigates away: a session that came from Auth0 ends there too.
    await endAuth0Session().catch(() => false);
  }, []);

  // An older session cookie predating feature flags returns a user without them; falling back
  // to all-enabled keeps that session usable rather than hiding the entire app.
  const features = user?.features ?? allFeaturesEnabled();
  const hasFeature = useCallback((feature: UiFeature) => features[feature] !== false, [features]);

  // Same fallback for the same reason: a session cookie predating capabilities returns a user
  // without them, and defaulting to all-enabled keeps that session showing everything rather than
  // silently stripping controls.
  const capabilities = user?.capabilities ?? allCapabilitiesEnabled();
  const hasCapability = useCallback(
    (capability: UiCapability) => capabilities[capability] !== false,
    [capabilities],
  );

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        features,
        hasFeature,
        capabilities,
        hasCapability,
        // Only ever a hint for what to render. The API re-checks it from the database on every
        // /admin request, so a stale or tampered client value grants nothing.
        isSystemAdmin: user?.isSystemAdmin === true,
        loginWithAuth0,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
